"use client";

import React, { useEffect, useRef, useState } from "react";

interface CameraCaptureProps {
  /** Callback invoked with captured image data URL */
  onCapture: (dataUrl: string) => void;
}

export default function CameraCapture({ onCapture }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let mounted = true;
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const constraints = isMobile
      ? { video: { facingMode: { ideal: "environment" } } }
      : { video: true };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .catch(() => navigator.mediaDevices.getUserMedia({ video: true }))
      .then((stream) => {
        if (!mounted) {
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {
            /* ignore */
          });
        }
      })
      .catch(() => {
        // ignore failure; user may deny permission
      })
      .finally(() => setInitializing(false));

    return () => {
      mounted = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/png");
    onCapture(dataUrl);
  };

  return (
    <div className="w-full flex flex-col items-center gap-4">
      <div className="aspect-video w-full bg-muted rounded-md overflow-hidden">
        <video ref={videoRef} className="w-full h-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />
      </div>
      <button
        onClick={handleCapture}
        disabled={initializing}
        className="w-full"
      >
        Capture Photo
      </button>
    </div>
  );
}
